// Staff GROUPS tab — the Identity-Center model. An operator assigns a role to a
// GROUP (not just a single person): a group has MEMBERS (users) and ROLE
// ASSIGNMENTS (a role, optionally scoped to one facility; null = org-wide).
//
// DESIGN (matches the approved R2 Roles screen): a SUMMARY-card list (NO giant
// tables) + a FOCUSED detail/editor dialog. One summary card per group shows the
// NAME + an honest member count ("3 members") + a one-line role summary
// ("Manager · Front Desk" or "No roles yet"). Clicking a card opens the focused
// detail with two sections: Members and Roles.
//
// HONESTY RULES (STRICT):
//   - Never render a raw group_id / role_id / assignment_id / Firebase UID as if
//     it were a name. A member's only human-readable fact is its user_id (there
//     is NO staff display-name endpoint yet — a documented follow-up), so a member
//     is shown by an honest LABELED + TRUNCATED identifier, never the bare uid.
//   - An org-wide assignment (facility_id null) reads "Org-wide", never "null".
//   - member_count + role summary come from the server (the detail fetches) — never
//     fabricated.
//
// SERVER-AUTHORITATIVE role picker (architecture rule: no client-side authz):
// a role whose permissions are NOT a subset of the caller's grantable set (from
// getGrantablePermissions) is DISABLED in the picker when the caller is not the
// owner — never a hardcoded client list. A backend 403 on assign surfaces honestly
// (native notification) and leaves the list UNCHANGED (no optimistic; no refetch on failure).

import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { ApiError } from "@bw/shared/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { notifications } from "@/app/notifications";

import { listFacilitiesByOrg, type FacilityListItem } from "@/libs/facilityApi";
import {
  addGroupMember,
  assignRoleToGroup,
  createGroup,
  getGrantablePermissions,
  listGroupMembers,
  listGroupRoleAssignments,
  listGroups,
  listOrgRoles,
  removeGroupMember,
  revokeRoleFromGroup,
  type GrantablePermissions,
  type GroupRoleAssignment,
  type OrgRole,
  type StaffGroup,
  type StaffGroupMember,
} from "@/libs/permissionsApi";

export interface GroupsTabProps {
  orgId: string;
}

// Friendly label for the four fixed facility roles; falls back to the role's own
// name (custom roles) so a raw role_id is never shown.
const ROLE_LABELS: Record<string, string> = {
  owner: "Owner",
  manager: "Manager",
  front_desk: "Front desk",
  coach: "Coach",
  member: "Member",
};

function roleLabel(role: OrgRole | undefined): string {
  if (!role) return "Unknown role";
  return ROLE_LABELS[role.name.toLowerCase()] ?? role.name;
}

function memberCountLabel(count: number): string {
  return count === 1 ? "1 member" : `${count} members`;
}

// HONEST member identity: we have NO display-name endpoint for members, so we
// show a clearly labeled, truncated identifier — never the bare full uid passed
// off as a name. If the value looks like an email (entered this session) we show
// it verbatim (it IS human-readable); otherwise we truncate the opaque uid.
function memberIdentity(userId: string | null | undefined): string {
  if (!userId) return "Erased account";
  if (userId.includes("@")) return userId;
  const head = userId.slice(0, 8);
  return `${head}…`;
}

export function GroupsTab({ orgId }: GroupsTabProps) {
  const [groups, setGroups] = useState<StaffGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-group summary (member count + assigned-role names) fetched for the list.
  // Keyed by group_id. Comes from the server (members + role assignments), never
  // fabricated. `failed: true` means the per-group fetch errored — the count /
  // roles are UNKNOWN (NOT a true zero), so the card must show an honest
  // placeholder ("— members" / "Members unavailable") rather than a confident
  // "0 members" / "No roles yet" that could mislead an operator into deleting a
  // live group.
  type GroupSummary =
    | { failed: false; memberCount: number; roleNames: string[] }
    | { failed: true };
  const [summaries, setSummaries] = useState<Record<string, GroupSummary>>({});

  // Org roles (role_id -> name) + facilities (facility_id -> name) resolve the
  // honest labels used across the list summary and the detail. Both are
  // best-effort: a failure degrades to a generic honest label, never a crash.
  const [roles, setRoles] = useState<OrgRole[]>([]);
  const [facilities, setFacilities] = useState<FacilityListItem[]>([]);
  const [grantable, setGrantable] = useState<GrantablePermissions | null>(null);

  // Create-group dialog.
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Focused detail.
  const [detailGroup, setDetailGroup] = useState<StaffGroup | null>(null);

  const rolesById = useMemo(() => {
    const map = new Map<string, OrgRole>();
    for (const r of roles) map.set(r.role_id, r);
    return map;
  }, [roles]);

  const facilitiesById = useMemo(() => {
    const map = new Map<string, FacilityListItem>();
    for (const f of facilities) map.set(f.facility_id, f);
    return map;
  }, [facilities]);

  // Load the groups list and, for each, the per-group summary (member count +
  // assigned-role names). The per-group summary fetches are best-effort, but a
  // FAILED summary is recorded as `failed: true` (UNKNOWN) — NOT silently
  // coerced to a fabricated zero. The card renders an honest placeholder for an
  // unknown count.
  const load = useCallback(async () => {
    const data = await listGroups(orgId);
    setGroups(data);
    const entries = await Promise.all(
      data.map(async (g): Promise<readonly [string, GroupSummary]> => {
        try {
          const [members, assignments] = await Promise.all([
            listGroupMembers(g.group_id),
            listGroupRoleAssignments(g.group_id),
          ]);
          return [
            g.group_id,
            {
              failed: false,
              memberCount: members.length,
              roleNames: assignments.map((a) => a.role_id),
            },
          ] as const;
        } catch {
          // UNKNOWN — do NOT fabricate a zero. The card shows an honest
          // "— members" / "Members unavailable" placeholder.
          return [g.group_id, { failed: true }] as const;
        }
      }),
    );
    setSummaries(Object.fromEntries(entries));
  }, [orgId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    // The groups list is the ONLY hard dependency that gates the loading
    // skeleton. As soon as it resolves the list renders — even if the
    // best-effort context (facilities / roles / grantable) is still pending or
    // hung. The role picker stays fail-closed (disabled) until grantable
    // resolves, so a slow grantable can never block the list nor weaken the
    // server-authoritative lock.
    (async () => {
      try {
        await load();
        if (!cancelled) setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Failed to load groups.",
          );
          setLoading(false);
        }
      }
    })();
    // Best-effort context fetched in the BACKGROUND — never awaited before the
    // list renders. Each degrades independently ([] / null) on rejection.
    (async () => {
      const [facilityRows, roleRows, grant] = await Promise.allSettled([
        listFacilitiesByOrg(orgId),
        listOrgRoles(orgId),
        getGrantablePermissions(orgId),
      ]).then((results) => [
        results[0].status === "fulfilled" ? results[0].value : [],
        results[1].status === "fulfilled" ? results[1].value : [],
        results[2].status === "fulfilled" ? results[2].value : null,
      ]);
      if (cancelled) return;
      setFacilities(facilityRows as FacilityListItem[]);
      setRoles(roleRows as OrgRole[]);
      setGrantable(grant as GrantablePermissions | null);
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, load]);

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      await createGroup(orgId, { name });
      await load();
      setCreateOpen(false);
      setNewName("");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Could not create group.";
      setCreateError(message);
      notifications.error(message);
    } finally {
      setCreating(false);
    }
  };

  function roleSummaryLabel(group: StaffGroup): string {
    const summary = summaries[group.group_id];
    // Unknown (fetch pending or failed) → honest placeholder, NOT "No roles
    // yet". Asserting "No roles yet" on a transient error could mislead an
    // operator into deleting a group that actually holds live assignments.
    if (!summary) return "Loading…";
    if (summary.failed) return "Roles unavailable";
    if (summary.roleNames.length === 0) return "No roles yet";
    const groupNameLower = group.name.toLowerCase();
    const names = summary.roleNames
      .map((roleId) => roleLabel(rolesById.get(roleId)))
      // Dedupe (a role can be assigned org-wide AND scoped to a facility).
      .filter((name, i, all) => all.indexOf(name) === i)
      // Don't echo a role name that the group's OWN name already conveys — a
      // "Front Desk Team" group needn't repeat "Front Desk" in its teaser
      // summary. The focused detail still lists every assignment in full, so no
      // information is lost; this only keeps the one-line list teaser from
      // redundantly restating the group's identity.
      .filter((name) => !groupNameLower.includes(name.toLowerCase()));
    // If every assigned role's name was already implied by the group name, the
    // group still HAS roles — say so honestly rather than "No roles yet".
    if (names.length === 0) return "Roles assigned";
    return names.join(" · ");
  }

  return (
    <Stack spacing={2} sx={{ pt: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
      >
        <Typography color="text.secondary" variant="body2">
          Groups bundle people so a role can be granted to the whole team at
          once.
        </Typography>
        <Button
          disabled={loading}
          onClick={() => {
            setNewName("");
            setCreateError(null);
            setCreateOpen(true);
          }}
          size="small"
          startIcon={<AddIcon />}
          variant="contained"
        >
          New group
        </Button>
      </Stack>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : loading ? (
        <Box
          aria-label="Loading groups"
          role="status"
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          }}
        >
          {[0, 1, 2, 3].map((i) => (
            <Paper key={i} variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={0.75}>
                <Skeleton height={24} width={128} />
                <Skeleton height={20} width={160} />
                <Skeleton height={18} width={80} />
              </Stack>
            </Paper>
          ))}
        </Box>
      ) : groups.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ borderStyle: "dashed", p: 5, textAlign: "center" }}
        >
          <GroupsOutlinedIcon color="action" sx={{ fontSize: 36 }} />
          <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 700 }}>
            No groups yet
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Create a group to grant a role to several people at once.
          </Typography>
          <Button
            onClick={() => {
              setNewName("");
              setCreateError(null);
              setCreateOpen(true);
            }}
            size="small"
            startIcon={<AddIcon />}
            sx={{ mt: 2 }}
            variant="outlined"
          >
            New group
          </Button>
        </Paper>
      ) : (
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
          }}
        >
          {groups.map((group) => {
            const summary = summaries[group.group_id];
            return (
              <Paper
                key={group.group_id}
                onClick={() => setDetailGroup(group)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    setDetailGroup(group);
                  }
                }}
                role="button"
                tabIndex={0}
                variant="outlined"
                sx={{
                  cursor: "pointer",
                  p: 2,
                  transition: (theme) =>
                    theme.transitions.create([
                      "background-color",
                      "border-color",
                    ]),
                  "&:hover": {
                    bgcolor: "action.hover",
                    borderColor: "primary.main",
                  },
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: 2,
                  },
                }}
              >
                <Stack spacing={0.5}>
                  <Typography
                    noWrap
                    variant="subtitle1"
                    sx={{ fontWeight: 700 }}
                  >
                    {group.name}
                  </Typography>
                  <Typography color="text.secondary" noWrap variant="body2">
                    {roleSummaryLabel(group)}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ alignItems: "center" }}
                  >
                    <GroupsOutlinedIcon color="action" sx={{ fontSize: 16 }} />
                    {/* Honest count: a true number only when the per-group fetch
                        succeeded. Unknown (pending/failed) shows "— members" /
                        "Members unavailable" — never a fabricated "0 members". */}
                    <Typography color="text.secondary" variant="caption">
                      {!summary
                        ? "— members"
                        : summary.failed
                          ? "Members unavailable"
                          : memberCountLabel(summary.memberCount)}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Create-group dialog */}
      {createOpen ? (
        <Dialog
          fullWidth
          maxWidth="xs"
          onClose={() => {
            if (!creating) setCreateOpen(false);
          }}
          open
        >
          <DialogTitle>New group</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2.5 }}>
              Name the group. You can add members and assign roles after
              it&apos;s created.
            </DialogContentText>
            <Stack spacing={2}>
              {createError ? (
                <Alert severity="error">{createError}</Alert>
              ) : null}
              <TextField
                disabled={creating}
                fullWidth
                id="group-name"
                label="Group name"
                onChange={(event) => setNewName(event.target.value)}
                placeholder="e.g. Front desk team"
                value={newName}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={creating} onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              disabled={creating || !newName.trim()}
              onClick={handleCreate}
              variant="contained"
            >
              {creating ? "Creating…" : "Create group"}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}

      {/* Focused group detail (members + roles) */}
      <GroupDetailDialog
        group={detailGroup}
        roles={roles}
        rolesById={rolesById}
        facilitiesById={facilitiesById}
        facilities={facilities}
        grantable={grantable}
        onOpenChange={(open) => {
          if (!open) setDetailGroup(null);
        }}
        onChanged={load}
      />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Focused group detail — two sections (Members + Roles) inside one dialog.
// ---------------------------------------------------------------------------

interface GroupDetailDialogProps {
  group: StaffGroup | null;
  roles: OrgRole[];
  rolesById: Map<string, OrgRole>;
  facilitiesById: Map<string, FacilityListItem>;
  facilities: FacilityListItem[];
  grantable: GrantablePermissions | null;
  onOpenChange: (open: boolean) => void;
  // Called after a member/role mutation so the parent list summary refetches.
  onChanged: () => void | Promise<void>;
}

function GroupDetailDialog({
  group,
  roles,
  rolesById,
  facilitiesById,
  facilities,
  grantable,
  onOpenChange,
  onChanged,
}: GroupDetailDialogProps) {
  const open = group !== null;
  const groupId = group?.group_id ?? "";

  const [members, setMembers] = useState<StaffGroupMember[]>([]);
  const [assignments, setAssignments] = useState<GroupRoleAssignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Add-member input.
  const [memberInput, setMemberInput] = useState("");
  const [addingMember, setAddingMember] = useState(false);

  // In-flight per-row remove/revoke guards (re-entrancy).
  const [busyMember, setBusyMember] = useState<string | null>(null);
  const [busyAssignment, setBusyAssignment] = useState<string | null>(null);

  // Role picker.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedRoleId, setPickedRoleId] = useState<string>("");
  const [pickedFacilityId, setPickedFacilityId] = useState<string>("");
  const [assigning, setAssigning] = useState(false);

  // Refetch the two detail sections. Tracked separately from the parent list so
  // a mutation refetches the detail (no optimistic) without depending on the
  // list summary fetch.
  const reload = useCallback(async () => {
    if (!groupId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const [memberRows, assignmentRows] = await Promise.all([
        listGroupMembers(groupId),
        listGroupRoleAssignments(groupId),
      ]);
      setMembers(memberRows);
      setAssignments(assignmentRows);
    } catch (err) {
      setLoadError(
        err instanceof Error ? err.message : "Failed to load group.",
      );
    } finally {
      setLoading(false);
    }
  }, [groupId]);

  useEffect(() => {
    if (!open) return;
    setMemberInput("");
    setPickerOpen(false);
    setPickedRoleId("");
    setPickedFacilityId("");
    void reload();
  }, [open, reload]);

  const facilityScopeLabel = (
    facilityId: string | null | undefined,
  ): string => {
    if (!facilityId) return "Org-wide";
    const facility = facilitiesById.get(facilityId);
    return facility ? facility.name : "Facility-scoped";
  };

  // A role is grantable by this caller iff its permissions are a subset of the
  // caller's grantable set (owner → everything). Server-authoritative — never a
  // hardcoded client list. A non-grantable role is disabled in the picker.
  const isRoleGrantable = useCallback(
    (role: OrgRole): boolean => {
      if (!grantable) return false; // fail closed until the server answers
      if (grantable.is_owner) return true;
      const allowed = new Set(grantable.permissions);
      return role.permissions.every((p) => allowed.has(p));
    },
    [grantable],
  );

  const handleAddMember = async () => {
    const who = memberInput.trim();
    if (!who || addingMember) return;
    setAddingMember(true);
    try {
      await addGroupMember(groupId, { user_id_or_email: who });
      setMemberInput("");
      await reload();
      await onChanged();
    } catch (err) {
      notifications.error(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not add member.",
      );
    } finally {
      setAddingMember(false);
    }
  };

  const handleRemoveMember = async (member: StaffGroupMember) => {
    if (busyMember) return;
    if (!member.user_id) {
      notifications.error("Erased account cannot be removed from this group.");
      return;
    }
    setBusyMember(member.member_id);
    try {
      await removeGroupMember(groupId, member.user_id);
      await reload();
      await onChanged();
    } catch (err) {
      notifications.error(
        err instanceof Error ? err.message : "Could not remove member.",
      );
    } finally {
      setBusyMember(null);
    }
  };

  const handleAssignRole = async () => {
    if (!pickedRoleId || assigning) return;
    // Re-validate grantability at submit (defense-in-depth; the backend is the
    // real authority). Fail CLOSED: if the role can't be resolved (roles context
    // not loaded) OR it isn't grantable, do NOT POST. An undefined lookup is
    // unknown grantability, which must block — never assume grantable.
    const role = rolesById.get(pickedRoleId);
    if (!role || !isRoleGrantable(role)) return;
    setAssigning(true);
    try {
      await assignRoleToGroup(groupId, {
        role_id: pickedRoleId,
        // Org-wide → omit facility_id ENTIRELY (never send "null").
        ...(pickedFacilityId ? { facility_id: pickedFacilityId } : {}),
      });
      // Success → refetch (no optimistic insert) so the new assignment shows.
      await reload();
      await onChanged();
    } catch (err) {
      // No optimistic insert: surface the error honestly and leave the existing
      // list UNCHANGED — do NOT refetch-replace on failure.
      notifications.error(
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Could not assign role.",
      );
    } finally {
      // Close the picker either way: on success the assignment is now in the
      // list; on failure the operator sees the notifications and the unchanged list.
      // Closing also collapses the picker's option list so a role name doesn't
      // appear both as a pickable option AND as an existing assignment row.
      setPickerOpen(false);
      setPickedRoleId("");
      setPickedFacilityId("");
      setAssigning(false);
    }
  };

  const handleRevoke = async (assignment: GroupRoleAssignment) => {
    if (busyAssignment) return;
    setBusyAssignment(assignment.assignment_id);
    try {
      await revokeRoleFromGroup(groupId, assignment.assignment_id);
      await reload();
      await onChanged();
    } catch (err) {
      notifications.error(
        err instanceof Error ? err.message : "Could not revoke role.",
      );
    } finally {
      setBusyAssignment(null);
    }
  };

  if (!open) return null;

  return (
    <Dialog
      aria-describedby="group-detail-description"
      fullWidth
      maxWidth="sm"
      onClose={() => onOpenChange(false)}
      open
    >
      <DialogTitle>{group?.name ?? "Group"}</DialogTitle>
      <DialogContent dividers sx={{ maxHeight: "75vh" }}>
        <DialogContentText id="group-detail-description" sx={{ mb: 2.5 }}>
          Manage who belongs to this group and what it can do.
        </DialogContentText>

        <Stack spacing={3}>
          {loadError ? <Alert severity="error">{loadError}</Alert> : null}

          {/* ----- Members ----- */}
          <Box component="section">
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "center", mb: 1.5 }}
            >
              <GroupsOutlinedIcon color="action" fontSize="small" />
              <Typography
                component="h3"
                variant="subtitle2"
                sx={{ fontWeight: 700 }}
              >
                Members
              </Typography>
            </Stack>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ alignItems: { sm: "flex-start" } }}
            >
              <TextField
                autoComplete="off"
                disabled={addingMember}
                fullWidth
                id="group-member-input"
                label="Email or user ID"
                onChange={(event) => setMemberInput(event.target.value)}
                placeholder="teammate@example.com"
                size="small"
                value={memberInput}
              />
              <Button
                disabled={addingMember || !memberInput.trim()}
                onClick={handleAddMember}
                size="small"
                sx={{ flexShrink: 0, minHeight: 40 }}
                variant="contained"
              >
                {addingMember ? "Adding…" : "Add member"}
              </Button>
            </Stack>

            <Box sx={{ mt: 1.5 }}>
              {loading ? (
                <Stack aria-label="Loading members" role="status" spacing={1}>
                  <Skeleton height={40} />
                  <Skeleton height={40} />
                </Stack>
              ) : members.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No members yet.
                </Typography>
              ) : (
                <List disablePadding sx={{ display: "grid", gap: 0.75 }}>
                  {members.map((member) => (
                    <ListItem
                      key={member.member_id}
                      secondaryAction={
                        <IconButton
                          aria-label="Remove"
                          color="error"
                          disabled={
                            !member.user_id || busyMember === member.member_id
                          }
                          edge="end"
                          onClick={() => handleRemoveMember(member)}
                          size="small"
                          title={
                            member.user_id
                              ? "Remove from group"
                              : "Erased account cannot be removed"
                          }
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      }
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1.5,
                        pr: 6,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ alignItems: "center", minWidth: 0 }}
                      >
                        <VerifiedUserOutlinedIcon
                          color="primary"
                          fontSize="small"
                        />
                        <Typography noWrap variant="body2">
                          <Box
                            component="span"
                            sx={{ color: "text.secondary" }}
                          >
                            User{" "}
                          </Box>
                          <Box
                            component="span"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {memberIdentity(member.user_id)}
                          </Box>
                        </Typography>
                      </Stack>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>

          {/* ----- Roles ----- */}
          <Box
            component="section"
            sx={{ borderTop: 1, borderColor: "divider", pt: 2.5 }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: "center",
                justifyContent: "space-between",
                mb: 1.5,
              }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                <ShieldOutlinedIcon color="action" fontSize="small" />
                <Typography
                  component="h3"
                  variant="subtitle2"
                  sx={{ fontWeight: 700 }}
                >
                  Roles
                </Typography>
              </Stack>
              {/* The toggle that OPENS the picker. It is hidden while the picker
                  is open so the picker's own "Assign role" submit is the single
                  button by that name. */}
              {!pickerOpen ? (
                <Button
                  onClick={() => {
                    setPickerOpen(true);
                    setPickedRoleId("");
                    setPickedFacilityId("");
                  }}
                  size="small"
                  startIcon={<AddIcon />}
                  variant="outlined"
                >
                  Assign role
                </Button>
              ) : null}
            </Stack>

            {pickerOpen ? (
              <RolePicker
                roles={roles}
                facilities={facilities}
                pickedRoleId={pickedRoleId}
                pickedFacilityId={pickedFacilityId}
                isRoleGrantable={isRoleGrantable}
                onPickRole={setPickedRoleId}
                onPickFacility={setPickedFacilityId}
                onAssign={handleAssignRole}
                onCancel={() => {
                  setPickerOpen(false);
                  setPickedRoleId("");
                  setPickedFacilityId("");
                }}
                assigning={assigning}
              />
            ) : null}

            <Box sx={{ mt: pickerOpen ? 1.5 : 0 }}>
              {loading ? (
                <Stack aria-label="Loading roles" role="status" spacing={1}>
                  <Skeleton height={40} />
                  <Skeleton height={40} />
                </Stack>
              ) : assignments.length === 0 ? (
                <Typography color="text.secondary" variant="body2">
                  No roles yet.
                </Typography>
              ) : (
                <List disablePadding sx={{ display: "grid", gap: 0.75 }}>
                  {assignments.map((assignment) => (
                    <ListItem
                      key={assignment.assignment_id}
                      secondaryAction={
                        <IconButton
                          aria-label="Revoke role"
                          color="error"
                          disabled={busyAssignment === assignment.assignment_id}
                          edge="end"
                          onClick={() => handleRevoke(assignment)}
                          size="small"
                          title="Revoke role"
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      }
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1.5,
                        pr: 6,
                      }}
                    >
                      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                        <Typography
                          noWrap
                          variant="body2"
                          sx={{ fontWeight: 700 }}
                        >
                          {roleLabel(rolesById.get(assignment.role_id))}
                        </Typography>
                        <Typography
                          color="text.secondary"
                          noWrap
                          variant="caption"
                        >
                          {facilityScopeLabel(assignment.facility_id)}
                        </Typography>
                      </Stack>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onOpenChange(false)}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Role picker — a server-authoritative listbox. Each role is a role="option"
// button; a role the caller cannot grant (per grantable) is disabled. An
// optional facility scope select (default = org-wide → no facility_id sent).
// ---------------------------------------------------------------------------

interface RolePickerProps {
  roles: OrgRole[];
  facilities: FacilityListItem[];
  pickedRoleId: string;
  pickedFacilityId: string;
  isRoleGrantable: (role: OrgRole) => boolean;
  onPickRole: (roleId: string) => void;
  onPickFacility: (facilityId: string) => void;
  onAssign: () => void;
  onCancel: () => void;
  assigning: boolean;
}

function RolePicker({
  roles,
  facilities,
  pickedRoleId,
  pickedFacilityId,
  isRoleGrantable,
  onPickRole,
  onPickFacility,
  onAssign,
  onCancel,
  assigning,
}: RolePickerProps) {
  const listRef = useRef<HTMLUListElement>(null);
  return (
    <Paper variant="outlined" sx={{ bgcolor: "action.hover", p: 1.5 }}>
      <Stack spacing={1.5}>
        <Box>
          <Typography
            color="text.secondary"
            variant="caption"
            sx={{ fontWeight: 700 }}
          >
            Role
          </Typography>
          <List
            aria-label="Roles"
            disablePadding
            ref={listRef}
            role="listbox"
            sx={{ display: "grid", gap: 0.5, mt: 0.5 }}
          >
            {roles.length === 0 ? (
              <ListItem sx={{ px: 1, py: 0.75 }}>
                <Typography color="text.secondary" variant="body2">
                  Create a role first.
                </Typography>
              </ListItem>
            ) : (
              roles.map((role) => {
                const grantableRole = isRoleGrantable(role);
                const selected = pickedRoleId === role.role_id;
                return (
                  <ListItem disablePadding key={role.role_id}>
                    <Button
                      aria-disabled={!grantableRole}
                      aria-selected={selected}
                      color="inherit"
                      data-disabled={grantableRole ? undefined : ""}
                      disabled={!grantableRole}
                      fullWidth
                      onClick={() => grantableRole && onPickRole(role.role_id)}
                      role="option"
                      sx={{
                        justifyContent: "space-between",
                        minWidth: 0,
                        px: 1.25,
                        textTransform: "none",
                      }}
                      title={
                        grantableRole
                          ? undefined
                          : "You can't grant this role — it includes permissions you don't hold."
                      }
                      type="button"
                      variant={selected ? "contained" : "text"}
                    >
                      <Typography noWrap variant="body2">
                        {roleLabel(role)}
                      </Typography>
                      {!grantableRole ? (
                        <Typography color="text.secondary" variant="caption">
                          Owner only
                        </Typography>
                      ) : null}
                    </Button>
                  </ListItem>
                );
              })
            )}
          </List>
        </Box>

        <TextField
          disabled={assigning}
          fullWidth
          id="group-role-scope"
          label="Scope"
          onChange={(event) => onPickFacility(event.target.value)}
          select
          size="small"
          value={pickedFacilityId}
        >
          <MenuItem value="">Org-wide (all facilities)</MenuItem>
          {facilities.map((facility) => (
            <MenuItem key={facility.facility_id} value={facility.facility_id}>
              {facility.name}
            </MenuItem>
          ))}
        </TextField>

        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
          <Button disabled={assigning} onClick={onCancel} size="small">
            Cancel
          </Button>
          <Button
            disabled={assigning || !pickedRoleId}
            onClick={onAssign}
            size="small"
            variant="contained"
          >
            {assigning ? "Assigning…" : "Assign role"}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}
